/**
 * ── AS SEIS ESTRUTURAS DA TELA DE ENTRAR (leva L1 da bancada) ────────────────
 *
 * O dono: *«o usuário vai poder escolher estruturas pré-definidas e mudar cor,
 * fotos, ícones, fonte, tamanho, imagem… seis estruturas para a tela de login».*
 *
 * Cada estrutura é uma COMPOSIÇÃO — onde a voz da marca fica, o que é superfície
 * e o que é ar, como o formulário se apoia —, e não a mesma tela com a foto
 * noutro canto. As duas primeiras são as que já existiam (`orientacao`
 * `centro`/`lateral`), pixel-iguais: é a compatibilidade. Sem import: o aluno lê.
 *
 * Fontes: pilhas de SISTEMA custam zero byte; a Literata já é paga pelo leitor
 * (`lib/fonte-de-leitura.ts`) e só se carrega quando escolhida — nada entra no
 * `index.html` (V13: a tela de entrar É o pedaço de entrada).
 */
export const ESTRUTURAS_DE_ENTRADA = ['sobria', 'lateral', 'veu', 'cartao', 'editorial', 'faixa'] as const;
export type EstruturaDeEntrada = (typeof ESTRUTURAS_DE_ENTRADA)[number];

export interface DescricaoDaEstrutura {
  nome: string;
  frase: string;
  /** O que a foto é para esta composição. */
  foto: 'ignora' | 'obrigatoria' | 'recomendada' | 'opcional';
  /** Onde a chamada cai em relação ao formulário. */
  chamada: 'acima' | 'abaixo' | 'faixa';
  usaVeu: boolean;
}

export const DESCRICAO_DA_ESTRUTURA: Record<EstruturaDeEntrada, DescricaoDaEstrutura> = {
  sobria: {
    nome: 'Sóbria',
    frase: 'Uma coluna no centro: o nome pequeno em cima, o formulário, a chamada embaixo. A de sempre.',
    foto: 'ignora',
    chamada: 'abaixo',
    usaVeu: false,
  },
  lateral: {
    nome: 'Lateral',
    frase: 'A foto ocupa a metade esquerda; o formulário fica à direita. No celular a foto some.',
    foto: 'obrigatoria',
    chamada: 'abaixo',
    usaVeu: false,
  },
  veu: {
    nome: 'Véu',
    frase: 'A foto sangra a tela inteira sob um véu regulável; a chamada vira o eyebrow do título, e o formulário se apoia direto no véu.',
    foto: 'recomendada',
    chamada: 'acima',
    usaVeu: true,
  },
  cartao: {
    nome: 'Cartão',
    frase: 'A foto inteira e um cartão flutuante deslocado para a direita, com a régua da casa no topo. No celular o cartão vira uma folha no rodapé.',
    foto: 'recomendada',
    chamada: 'acima',
    usaVeu: true,
  },
  editorial: {
    nome: 'Editorial',
    frase: 'Duas colunas iguais: a marca grande, a régua e a chamada à esquerda; o formulário num painel de superfície à direita. A tipografia é a identidade.',
    foto: 'opcional',
    chamada: 'acima',
    usaVeu: false,
  },
  faixa: {
    nome: 'Faixa',
    frase: 'Um cabeçalho de portal com a marca e a chamada; abaixo, o formulário e o «primeira vez» lado a lado. Com foto, a faixa cresce sobre ela.',
    foto: 'opcional',
    chamada: 'faixa',
    usaVeu: true,
  },
};

/**
 * As que exigem foto — uma lista à parte, e não `DESCRICAO_DA_ESTRUTURA[...].foto`,
 * para o aluno não levar as seis descrições (que só a bancada lê) no pedaço de
 * entrada. A cerca prova que as duas dizem a mesma coisa.
 */
export const ESTRUTURAS_QUE_EXIGEM_FOTO: readonly EstruturaDeEntrada[] = ['lateral'];

/**
 * A estrutura que vale: a gravada vence; sem ela, a orientação antiga decide
 * (`centro` → Sóbria, `lateral` → Lateral). Quem exige foto e não tem cai em
 * Sóbria — no servidor E no aluno, para os dois desenharem o mesmo.
 */
export function estruturaResolvida(entrada: {
  estrutura?: string | null;
  orientacao?: 'centro' | 'lateral' | string | null;
  imagem?: string | null;
}): EstruturaDeEntrada {
  const pedida = (ESTRUTURAS_DE_ENTRADA as readonly string[]).includes(entrada.estrutura ?? '')
    ? (entrada.estrutura as EstruturaDeEntrada)
    : entrada.orientacao === 'lateral'
      ? 'lateral'
      : 'sobria';
  if (ESTRUTURAS_QUE_EXIGEM_FOTO.includes(pedida) && !entrada.imagem) return 'sobria';
  return pedida;
}

export const FONTES_DA_ENTRADA = ['sistema', 'serifa', 'mono', 'literata'] as const;
export type FonteDaEntrada = (typeof FONTES_DA_ENTRADA)[number];

/** As pilhas — as três primeiras sem custo; a Literata só quando escolhida. */
export const PILHAS_DE_FONTE: Record<FonteDaEntrada, string> = {
  sistema: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serifa: "Georgia, 'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', 'Times New Roman', serif",
  mono: "ui-monospace, 'Cascadia Mono', 'Segoe UI Mono', Menlo, Consolas, monospace",
  literata: "'Literata', Georgia, 'Times New Roman', serif",
};

export const TAMANHOS_DA_ENTRADA = ['compacto', 'normal', 'amplo'] as const;
export type TamanhoDaEntrada = (typeof TAMANHOS_DA_ENTRADA)[number];
export const ESCALA_DA_ENTRADA: Record<TamanhoDaEntrada, number> = { compacto: 0.9, normal: 1, amplo: 1.15 };

/* ── cor: o mínimo de aritmética para o texto sobre o destaque ────────────── */

function canais(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminancia(hex: string): number {
  const c = canais(hex);
  if (!c) return 0;
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);
}

/** O que contrasta mais sobre um fundo: o escuro da casa ou o claro da casa. */
export function textoSobre(fundo: string): '#08130c' | '#f4f4f5' {
  /* O limiar é o ponto em que preto e branco contrastam IGUAL sobre o fundo
     ((L+0,05)/0,05 = 1,05/(L+0,05) → L ≈ 0,179). Acima, o escuro contrasta mais.
     Era 0,35: o laranja da casa (#f97316, L≈0,31) recebia branco a 2,6:1 quando
     o escuro dá 7:1 — o aviso da bancada acendia e não havia onde agir. */
  return luminancia(fundo) > 0.179 ? '#08130c' : '#f4f4f5';
}

/** Mistura por canal (o `color-mix(in srgb)` do CSS), `peso` é a parte de `b`. */
export function misturar(a: string, b: string, peso: number): string {
  const ca = canais(a);
  const cb = canais(b);
  if (!ca || !cb) return a;
  const p = Math.max(0, Math.min(1, peso));
  const h = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  return `#${h(ca[0] * (1 - p) + cb[0] * p)}${h(ca[1] * (1 - p) + cb[1] * p)}${h(ca[2] * (1 - p) + cb[2] * p)}`;
}
